import { Waypoint } from 'react-waypoint';

import { InfoV2Icon } from '@change/ds-icons';

import { Link, Separator, Text } from '@change/ds-core-ui/next';

import { Translate } from '@change-corgi/core/react/i18n';
import { Loader } from '@change-corgi/design-system-legacy/components/progressiveDisclosure';

import { useMarkModuleAsLoaded } from 'src/app/pages/starterDashboardV2/shared/contexts';
import type { WaypointCallbackArgs } from 'src/app/shared/components/layout/lazy';

import { RecommendationsCarousel } from './components/RecommendationsCarousel';
import { ShareUrlReskin } from './components/ShareUrl/index_new';
import { useRecommendations } from './hooks/useRecommendations';
import { useShareRecommendations } from './hooks/useShareRecommendations';

import type { Props } from './index';

export function RecommendationsVariant2Reskin({ slug, viewContext }: Props): JSX.Element | null {
	const {
		data: { shareRecommendationsLoading, shouldDisplayShareRecommendations, shareRecommendations },
	} = useShareRecommendations(slug);

	const { actions } = useRecommendations();

	useMarkModuleAsLoaded('Recommendations', !shareRecommendationsLoading && shouldDisplayShareRecommendations);

	if (shareRecommendationsLoading) {
		return <Loader size="m" my={64} mx="auto" data-testid="share-recommendations-loading" />;
	}

	if (!shouldDisplayShareRecommendations) {
		return null;
	}

	const firstRedditRec = shareRecommendations.reddit.at(0);
	const firstFacebookRec = shareRecommendations.facebook.at(0);

	return (
		<section aria-labelledby="recommendations-title" className="bg-background-base px-4 py-8 pr-0 md:p-12 md:pr-0">
			<Waypoint
				onPositionChange={(waypointCallbackArgs: WaypointCallbackArgs) => {
					actions.trackView(
						waypointCallbackArgs,
						firstFacebookRec?.metadata.source,
						firstRedditRec?.metadata.source,
						viewContext,
					);
				}}
			/>
			<div className="flex flex-col gap-6">
				<div className="flex flex-col gap-5 pr-4 md:pr-12">
					<div className="flex flex-col gap-3">
						<Text className="text-size-heading-xs md:text-size-heading-small">Share in recommended groups</Text>
						<Text variant="body">
							{actions.translateHtml('corgi.petition.starter_dashboard.recommendations.description_variant')}&nbsp;
							<Link
								as="span"
								variant="secondary"
								onClick={async () => {
									await actions.handleTipClick();
								}}
								className="cursor-pointer"
								data-qa="recommendations-tip-button"
							>
								<Text variant="body">
									<div className="flex flex-row items-center gap-1">
										<Translate value="corgi.petition.starter_dashboard.recommendations.sharing_tips" />
										<InfoV2Icon size="x-small" />
									</div>
								</Text>
							</Link>
						</Text>
					</div>
					<div className="w-full md:w-auto">
						<ShareUrlReskin viewContext={viewContext} />
					</div>
				</div>
				<div className="pr-4 md:pr-12">
					<Separator className="m-0" />
				</div>
				<RecommendationsCarousel shareRecommendations={shareRecommendations} isReskin viewContext={viewContext} />
			</div>
		</section>
	);
}
